// frontend/src/pages/OpportunitiesPage.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2026
// Contact:      dev@castle-traffic.co.uk
// Created:      2026-01-14
// Updated:      2026-09-25 10:45 BST
//
// Desktop resize regression fix (2026-09-25):
//   - Removed the viewport-derived ReactGridLayout `maxRows` ceiling.
//   - Desktop widgets may now grow vertically beyond the initial viewport and the page scrolls naturally.
//   - Layout persistence preserves x/y/w/h while preventing negative y positions.
//   - Page-owned widget positioning is no longer forced back inside viewport rows.
//   - Existing mobile single-column presentation and mobile sizing behaviour are unchanged.
//
// Description:
//   Opportunities workspace page (dashboard-style GridLayout).
//   • Uses react-grid-layout + WidgetWrapper + AddWidgetModal (same pattern as DashboardPage).
//   • Persists layout/widgets in localStorage (in-memory first pass; same as other dashboards).
//   • Portals modals at PAGE level (Create + Access), not inside widgets.
//   • Uses shared Axios instance (api) ONLY.
//
// Mobile Responsiveness (2026-09-04):
//   - Added a dedicated mobile presentation at <= 768px.
//   - Mobile renders configured Opportunity widgets in a natural single-column flow.
//   - Mobile ordering follows the persisted desktop layout position (y, then x).
//   - react-grid-layout drag/resize behaviour remains desktop-only.
//   - Mobile rendering never persists layout changes, so viewing Opportunities on a phone
//     cannot alter the user's desktop widget arrangement.
//   - Reflowed page title, signed-in context, New Opportunity, Add Widget, and Share / Access
//     controls for compact mobile widths.
//   - Reduced mobile page gutters while preserving opportunity data flows, page-owned modals,
//     access controls, widget migration, localStorage keys, and desktop layout behaviour.
//
// Notes:
//   - Default layout mirrors the original 3-column intent:
//       Stage progress full-width top
//       Left:   Inbox + List
//       Middle: Detail
//       Right:  Client summary + Financial + AI insights
//   - Widgets are wrapped in a scroll-safe container so resizing doesn't “break” content.
//   - No tenant_id accepted from client; auth handled by backend per canonical dependency.
//   - Includes a backward-compatible ID migration so prior localStorage layouts load.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import GridLayout from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

import api from "../api/axiosInstance";
import { useTheme } from "../context/ThemeContext.jsx";

import WidgetWrapper from "../components/WidgetWrapper";
import AddWidgetModal from "../components/AddWidgetModal";

import OpportunityStageProgressWidget from "../widgets/OpportunityStageProgressWidget.jsx";
import OpportunityInboxWidget from "../widgets/OpportunityInboxWidget.jsx";
import OpportunityListWidget from "../widgets/OpportunityListWidget.jsx";
import OpportunityDetailWidget from "../widgets/OpportunityDetailWidget.jsx";
import ClientContactSummaryWidget from "../widgets/ClientContactSummaryWidget.jsx";
import OpportunityFinancialSummaryWidget from "../widgets/OpportunityFinancialSummaryWidget.jsx";
import OpportunityAIInsightsWidget from "../widgets/OpportunityAIInsightsWidget.jsx";

import OpportunityCreateModal from "../components/OpportunityCreateModal.jsx";
import OpportunityAccessModal from "../components/OpportunityAccessModal.jsx";

import { markWidgetUsedIn } from "../widgets/WidgetRegistry";

const STORAGE_KEY_LAYOUT = "opportunitiesLayout";
const STORAGE_KEY_WIDGETS = "opportunitiesWidgets";

const MOBILE_BREAKPOINT = 768;

function useIsMobile(breakpoint = MOBILE_BREAKPOINT) {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(`(max-width: ${breakpoint}px)`).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;

    const mediaQuery = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const apply = () => setIsMobile(mediaQuery.matches);

    apply();

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", apply);
    } else {
      mediaQuery.addListener(apply);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener("change", apply);
      } else {
        mediaQuery.removeListener(apply);
      }
    };
  }, [breakpoint]);

  return isMobile;
}

const STAGES = ["qualification", "discovery", "proposal", "negotiation", "closed"];

const safeNum = (v) => {
  if (v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const normaliseLayout = (layout) =>
  (Array.isArray(layout) ? layout : []).map((item) => {
    const currentY = Number.isFinite(item?.y) ? item.y : 0;
    return { ...item, y: Math.max(0, currentY) };
  });

/**
 * ID migration:
 * Old IDs (used by earlier iterations) -> New IDs (must match widgets.name in DB)
 */
const ID_MIGRATION = Object.freeze({
  opp_stage_progress: "opportunity_stage_progress",
  opp_inbox: "opportunity_inbox",
  opp_list: "opportunity_list",
  opp_detail: "opportunity_detail",
  client_contact_summary: "client_contact_summary", // already aligned
  opp_financial_summary: "financial_summary_widget",
  opp_ai_insights: "opportunity_ai_insights",
});

const migrateWidgetId = (id) => ID_MIGRATION[id] || id;

const migrateLayout = (layout) =>
  (layout || []).map((it) => {
    const oldId = it?.i;
    const newId = migrateWidgetId(oldId);
    return oldId === newId ? it : { ...it, i: newId };
  });

const migrateWidgetIds = (ids) => Array.from(new Set((ids || []).map(migrateWidgetId)));

/**
 * IMPORTANT:
 * These IDs are the canonical widget IDs for Opportunities and should match DB widgets.name.
 */
const defaultLayout = [
  // Stage progress (top row)
  { i: "opportunity_stage_progress", x: 0, y: 0, w: 12, h: 2, minW: 6, minH: 2, maxW: 12 },

  // Left column (4/12)
  { i: "opportunity_inbox", x: 0, y: 2, w: 4, h: 4, minW: 3, minH: 3, maxW: 6 },
  { i: "opportunity_list", x: 0, y: 6, w: 4, h: 8, minW: 3, minH: 4, maxW: 6 },

  // Middle column (5/12)
  { i: "opportunity_detail", x: 4, y: 2, w: 5, h: 12, minW: 4, minH: 8, maxW: 8 },

  // Right column (3/12)
  { i: "client_contact_summary", x: 9, y: 2, w: 3, h: 4, minW: 3, minH: 3, maxW: 6 },
  { i: "financial_summary_widget", x: 9, y: 6, w: 3, h: 5, minW: 3, minH: 3, maxW: 6 },
  { i: "opportunity_ai_insights", x: 9, y: 11, w: 3, h: 3, minW: 3, minH: 3, maxW: 6 },
];

export default function OpportunitiesPage() {
  const { theme } = useTheme();
  const isMobile = useIsMobile();

  // ---------- Page UI state ----------
  const [showAddModal, setShowAddModal] = useState(false);
  const [widgetCatalog, setWidgetCatalog] = useState([]);

  const [widgets, setWidgets] = useState({});
  const [layout, setLayout] = useState([]);

  const [gridWidth, setGridWidth] = useState(1200);
  const gridRef = useRef(null);

  const containerPadding = 24;
  const headerHeight = 64;
  const rowHeight = 40;
  // ---------- Opportunities state ----------
  const [me, setMe] = useState(null);

  const [list, setList] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [listErr, setListErr] = useState("");

  const [selectedId, setSelectedId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [selectedLoading, setSelectedLoading] = useState(false);
  const [selectedErr, setSelectedErr] = useState("");

  const [accessRows, setAccessRows] = useState([]);
  const [accessLoading, setAccessLoading] = useState(false);
  const [accessErr, setAccessErr] = useState("");

  const [actorResourceId, setActorResourceId] = useState(null);

  const [showCreate, setShowCreate] = useState(false);
  const [showAccess, setShowAccess] = useState(false);

  // ---------- Styling ----------
  const pageWrapStyle = useMemo(
    () => ({
      backgroundColor: "#f0f0f0",
      minHeight: isMobile ? "100%" : "100vh",
      overflowX: "hidden",
      paddingLeft: isMobile ? 12 : containerPadding,
      paddingRight: isMobile ? 12 : containerPadding,
      paddingTop: isMobile ? 4 : 16,
      paddingBottom: isMobile ? 16 : 24,
      boxSizing: "border-box",
      fontFamily: theme?.font_body || "inherit",
      color: theme?.text_color || "#111827",
    }),
    [isMobile, theme]
  );

  // Keep widgets scroll-safe even if inner widget forgets overflow
  const scrollSafe = useMemo(
    () => ({
      height: "100%",
      minHeight: 0,
      overflow: "auto",
      paddingRight: 2,
    }),
    []
  );

  // ---------- Width tracking (ResizeObserver on actual grid container) ----------
  useEffect(() => {
    const node = gridRef.current;
    if (!node) return;

    const updateWidth = () => {
      const nextWidth = Math.floor(node.getBoundingClientRect().width);
      if (nextWidth > 0) setGridWidth(nextWidth);
    };

    updateWidth();

    const observer = new ResizeObserver(updateWidth);
    observer.observe(node);

    return () => observer.disconnect();
  }, []);

  // ---------- Data loading ----------
  const loadMe = useCallback(async () => {
    try {
      const { data } = await api.get("/api/users/me");
      setMe(data);
    } catch {
      setMe(null);
    }
  }, []);

  const loadList = useCallback(async () => {
    setListLoading(true);
    setListErr("");
    try {
      const { data } = await api.get("/api/opportunities?limit=200&offset=0");
      const arr = Array.isArray(data) ? data : [];
      setList(arr);
      if (!selectedId && arr.length > 0) setSelectedId(arr[0].id);
    } catch (e) {
      setListErr(e?.response?.data?.detail || String(e?.message || "Failed to load opportunities"));
      setList([]);
    } finally {
      setListLoading(false);
    }
  }, [selectedId]);

  const loadSelected = useCallback(async (id) => {
    if (!id) {
      setSelected(null);
      return;
    }
    setSelectedLoading(true);
    setSelectedErr("");
    try {
      const { data } = await api.get(`/api/opportunities/${id}`);
      setSelected(data || null);
    } catch (e) {
      setSelectedErr(e?.response?.data?.detail || String(e?.message || "Failed to load opportunity"));
      setSelected(null);
    } finally {
      setSelectedLoading(false);
    }
  }, []);

  const loadAccess = useCallback(
    async (id) => {
      if (!id) {
        setAccessRows([]);
        setActorResourceId(null);
        return;
      }
      setAccessLoading(true);
      setAccessErr("");
      try {
        const { data } = await api.get(`/api/opportunities/${id}/access?include_revoked=true`);
        const rows = Array.isArray(data) ? data : [];
        setAccessRows(rows);

        // Actor resource id resolution
        let rid = null;

        if (me?.id) {
          const r1 =
            rows.find(
              (r) => r && r.revoked_at == null && r.granted_by_user_id === me.id && r.granted_by_resource_id
            ) || null;

          if (r1?.granted_by_resource_id) rid = Number(r1.granted_by_resource_id);

          if (!rid && selected?.owner_user_id === me.id && selected?.owner_resource_id) {
            rid = Number(selected.owner_resource_id);
          }
        }

        setActorResourceId(Number.isFinite(Number(rid)) ? Number(rid) : null);
      } catch (e) {
        setAccessErr(e?.response?.data?.detail || String(e?.message || "Failed to load access"));
        setAccessRows([]);
        setActorResourceId(null);
      } finally {
        setAccessLoading(false);
      }
    },
    [me?.id, selected?.owner_user_id, selected?.owner_resource_id]
  );

  useEffect(() => {
    loadMe();
    loadList();
  }, [loadMe, loadList]);

  useEffect(() => {
    if (!selectedId) return;
    loadSelected(selectedId);
  }, [selectedId, loadSelected]);

  useEffect(() => {
    if (!selectedId) return;
    loadAccess(selectedId);
  }, [selectedId, loadAccess]);

  const canEdit = useMemo(() => {
    if (!selectedId) return false;
    if (!actorResourceId) return false;
    return accessRows.some((r) => r && r.resource_id === actorResourceId && r.revoked_at == null && r.can_edit === true);
  }, [accessRows, actorResourceId, selectedId]);

  const refreshAll = useCallback(
    async (id) => {
      await Promise.all([loadList(), loadSelected(id), loadAccess(id)]);
    },
    [loadAccess, loadList, loadSelected]
  );

  // ---------- Mutations ----------
  const onCreate = useCallback(
    async (payload) => {
      const { data } = await api.post("/api/opportunities", payload);
      const newId = data?.id;
      await loadList();
      if (newId) {
        setSelectedId(newId);
        await Promise.all([loadSelected(newId), loadAccess(newId)]);
      }
    },
    [loadAccess, loadList, loadSelected]
  );

  const onPatch = useCallback(
    async (id, patch) => {
      const { data } = await api.patch(`/api/opportunities/${id}`, patch);
      setSelected(data);
      await loadList();
      await loadAccess(id);
    },
    [loadAccess, loadList]
  );

  const onShare = useCallback(
    async (id, resourceId, canEditFlag) => {
      await api.post(`/api/opportunities/${id}/share`, { resource_id: resourceId, can_edit: !!canEditFlag });
      await loadAccess(id);
    },
    [loadAccess]
  );

  const onRevoke = useCallback(
    async (id, resourceId, reason) => {
      await api.post(`/api/opportunities/${id}/revoke`, { resource_id: resourceId, reason: reason || "revoked" });
      await loadAccess(id);
    },
    [loadAccess]
  );

  // ---------- Derived data ----------
  const selectedAccessActive = useMemo(() => accessRows.filter((r) => r && r.revoked_at == null), [accessRows]);

  const inboxItems = useMemo(() => {
    const now = Date.now();
    const MS_DAY = 24 * 60 * 60 * 1000;
    const isLaterStage = (stage) => ["proposal", "negotiation", "closed"].includes(String(stage || "").toLowerCase());

    return (list || [])
      .map((o) => {
        const updated = o?.updated_at ? Date.parse(o.updated_at) : null;
        const ageDays = updated ? Math.floor((now - updated) / MS_DAY) : null;
        const prob = safeNum(o?.probability);

        const flags = [];
        if (!o?.expected_close_date) flags.push("No close date");
        if (prob == null) flags.push("No probability");
        if (isLaterStage(o?.stage) && (prob === 0 || prob == null)) flags.push("Probability mismatch");
        if (ageDays != null && ageDays >= 30) flags.push(`Stale (${ageDays}d)`);

        return { o, flags, score: flags.length };
      })
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 8)
      .map((x) => ({
        id: x.o.id,
        name: x.o.name,
        stage: x.o.stage,
        status: x.o.status,
        updated_at: x.o.updated_at,
        flags: x.flags,
      }));
  }, [list]);

  // ---------- Widget resolving (dashboard pattern) ----------
  const resolveWidgetComponent = useCallback(
    (id) => {
      switch (id) {
        case "opportunity_stage_progress":
          return (
            <div style={scrollSafe}>
              <OpportunityStageProgressWidget
                stages={STAGES}
                opportunity={selected}
                canEdit={canEdit}
                onChangeStage={async (nextStage) => {
                  if (!selected?.id) return;
                  await onPatch(selected.id, { stage: nextStage });
                }}
                onChangeStatus={async (nextStatus) => {
                  if (!selected?.id) return;
                  await onPatch(selected.id, { status: nextStatus });
                }}
              />
            </div>
          );

        case "opportunity_inbox":
          return (
            <div style={scrollSafe}>
              <OpportunityInboxWidget
                items={inboxItems}
                loading={listLoading}
                error={listErr}
                selectedId={selectedId}
                onSelect={(id2) => setSelectedId(id2)}
              />
            </div>
          );

        case "opportunity_list":
          return (
            <div style={scrollSafe}>
              <OpportunityListWidget
                opportunities={list}
                loading={listLoading}
                error={listErr}
                selectedId={selectedId}
                onSelect={(id2) => setSelectedId(id2)}
                onRefresh={loadList}
              />
            </div>
          );

        case "opportunity_detail":
          return (
            <div style={scrollSafe}>
              <OpportunityDetailWidget
                opportunity={selected}
                loading={selectedLoading}
                error={selectedErr}
                canEdit={canEdit}
                accessLoading={accessLoading}
                accessError={accessErr}
                actorResourceId={actorResourceId}
                accessActiveRows={selectedAccessActive}
                onSave={async (patch) => {
                  if (!selected?.id) return;
                  await onPatch(selected.id, patch);
                }}
                onOpenAccess={() => setShowAccess(true)}
                onReload={async () => selected?.id && refreshAll(selected.id)}
              />
            </div>
          );

        case "client_contact_summary":
          return (
            <div style={scrollSafe}>
              <ClientContactSummaryWidget opportunity={selected} />
            </div>
          );

        case "financial_summary_widget":
          return (
            <div style={scrollSafe}>
              <OpportunityFinancialSummaryWidget opportunity={selected} />
            </div>
          );

        case "opportunity_ai_insights":
          return (
            <div style={scrollSafe}>
              <OpportunityAIInsightsWidget opportunity={selected} accessRows={accessRows} canEdit={canEdit} />
            </div>
          );

        default:
          return (
            <div style={{ padding: 12, color: theme?.danger || "#b91c1c", fontSize: 12 }}>
              Unknown widget: {String(id)}
            </div>
          );
      }
    },
    [
      accessErr,
      accessLoading,
      accessRows,
      actorResourceId,
      canEdit,
      inboxItems,
      list,
      listErr,
      listLoading,
      loadList,
      onPatch,
      refreshAll,
      scrollSafe,
      selected,
      selectedAccessActive,
      selectedErr,
      selectedId,
      selectedLoading,
      theme?.danger,
    ]
  );

  const getWidgetTitle = useCallback(
    (key) => {
      const found =
        (widgetCatalog || []).find((w) => w?.name === key) ||
        (widgetCatalog || []).find((w) => String(w?.id || "") === String(key));
      return (
        found?.friendly_name ||
        key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
      );
    },
    [widgetCatalog]
  );

  // ---------- Load widget catalog (same pattern as DashboardPage) ----------
  useEffect(() => {
    api
      .get("/api/widgets/")
      .then((res) => setWidgetCatalog(res.data))
      .catch(() =>
        setWidgetCatalog([
          { id: "opportunity_stage_progress", name: "opportunity_stage_progress", friendly_name: "Stage Progress", category: "Opportunities" },
          { id: "opportunity_inbox", name: "opportunity_inbox", friendly_name: "Opportunity Inbox", category: "Opportunities" },
          { id: "opportunity_list", name: "opportunity_list", friendly_name: "Opportunity List", category: "Opportunities" },
          { id: "opportunity_detail", name: "opportunity_detail", friendly_name: "Opportunity Details", category: "Opportunities" },
          { id: "client_contact_summary", name: "client_contact_summary", friendly_name: "Client & Contact Summary", category: "Opportunities" },
          { id: "financial_summary_widget", name: "financial_summary_widget", friendly_name: "Financial Summary", category: "Opportunities" },
          { id: "opportunity_ai_insights", name: "opportunity_ai_insights", friendly_name: "AI Insights", category: "Opportunities" },
        ])
      );
  }, []);

  // ---------- Bootstrap stored layout/widgets (with migration) ----------
  useEffect(() => {
    try {
      const storedLayoutRaw = JSON.parse(localStorage.getItem(STORAGE_KEY_LAYOUT));
      const storedWidgetIdsRaw = JSON.parse(localStorage.getItem(STORAGE_KEY_WIDGETS));

      const storedLayout = Array.isArray(storedLayoutRaw) ? storedLayoutRaw : null;
      const storedWidgetIds = Array.isArray(storedWidgetIdsRaw) ? storedWidgetIdsRaw : null;

      if (storedLayout && storedWidgetIds && storedWidgetIds.length > 0) {
        const migratedLayout = migrateLayout(storedLayout);
        const migratedIds = migrateWidgetIds(storedWidgetIds);

        const normalised = normaliseLayout(migratedLayout);
        setLayout(normalised);

        const comps = {};
        migratedIds.forEach((id) => {
          comps[id] = resolveWidgetComponent(id);
          markWidgetUsedIn(id, "opportunities");
        });
        setWidgets(comps);

        // Persist migrated state back to localStorage (silent self-heal)
        try {
          localStorage.setItem(STORAGE_KEY_LAYOUT, JSON.stringify(normalised));
          localStorage.setItem(STORAGE_KEY_WIDGETS, JSON.stringify(migratedIds));
        } catch {}

        return;
      }
    } catch {
      // fall through to defaults
    }

    setLayout(defaultLayout);
    const initialIds = defaultLayout.map((x) => x.i);

    const comps = {};
    initialIds.forEach((id) => {
      comps[id] = resolveWidgetComponent(id);
      markWidgetUsedIn(id, "opportunities");
    });
    setWidgets(comps);

    try {
      localStorage.setItem(STORAGE_KEY_LAYOUT, JSON.stringify(defaultLayout));
      localStorage.setItem(STORAGE_KEY_WIDGETS, JSON.stringify(initialIds));
    } catch {}
  }, [resolveWidgetComponent]);

  // Keep rendered widget components in sync when underlying props/data change
  useEffect(() => {
    setWidgets((prev) => {
      const next = {};
      Object.keys(prev || {}).forEach((id) => {
        next[id] = resolveWidgetComponent(id);
      });
      return next;
    });
  }, [resolveWidgetComponent]);

  // ---------- Add/remove/layout change ----------
  const handleAddWidget = useCallback(
    (widgetIdRaw) => {
      const widgetId = migrateWidgetId(widgetIdRaw);
      if (!widgetId) return;

      if (widgets[widgetId]) {
        alert("Widget already exists");
        return;
      }

      const comp = resolveWidgetComponent(widgetId);
      if (!comp) {
        alert("Unknown widget");
        return;
      }

      setWidgets((prev) => ({ ...prev, [widgetId]: comp }));
      markWidgetUsedIn(widgetId, "opportunities");

      setLayout((prev) => {
        const bottomY = prev.length === 0 ? 0 : Math.max(...prev.map((l) => (l?.y || 0) + (l?.h || 1)));
        const newY = Math.max(0, bottomY);

        const newItem = { i: widgetId, x: 0, y: newY, w: 6, h: 4, minW: 3, minH: 3, maxW: 12 };
        const newLayout = [...prev, newItem];

        try {
          localStorage.setItem(STORAGE_KEY_LAYOUT, JSON.stringify(newLayout));
          localStorage.setItem(STORAGE_KEY_WIDGETS, JSON.stringify([...Object.keys(widgets), widgetId]));
        } catch {}

        return newLayout;
      });

      setShowAddModal(false);
    },
    [resolveWidgetComponent, widgets]
  );

  const handleRemoveWidget = useCallback(
    (widgetId) => {
      // Guard: do not allow removal of core widgets (prevents “blank page”)
      const core = new Set(defaultLayout.map((x) => x.i));
      if (core.has(widgetId)) {
        alert("Core widget cannot be removed.");
        return;
      }

      setWidgets((prev) => {
        const clone = { ...prev };
        delete clone[widgetId];
        return clone;
      });

      setLayout((prev) => {
        const filtered = prev.filter((item) => item.i !== widgetId);
        try {
          localStorage.setItem(STORAGE_KEY_LAYOUT, JSON.stringify(filtered));
          localStorage.setItem(
            STORAGE_KEY_WIDGETS,
            JSON.stringify(Object.keys(widgets).filter((id) => id !== widgetId))
          );
        } catch {}
        return filtered;
      });
    },
    [widgets]
  );

  const onLayoutChange = useCallback(
    (newLayout) => {
      const normalised = normaliseLayout(newLayout);
      setLayout(normalised);
      try {
        localStorage.setItem(STORAGE_KEY_LAYOUT, JSON.stringify(normalised));
        localStorage.setItem(STORAGE_KEY_WIDGETS, JSON.stringify(Object.keys(widgets)));
      } catch {}
    },
    [widgets]
  );

  /*
   * Mobile is a presentation of the existing Opportunities configuration,
   * not a second editable layout. Follow the saved desktop visual order
   * without persisting mobile positions back into opportunitiesLayout.
   */
  const mobileWidgetEntries = Object.entries(widgets).sort(([a], [b]) => {
    const aIndex = layout.findIndex((item) => item.i === a);
    const bIndex = layout.findIndex((item) => item.i === b);

    const aLayout = aIndex >= 0 ? layout[aIndex] : null;
    const bLayout = bIndex >= 0 ? layout[bIndex] : null;

    const ay = Number.isFinite(aLayout?.y) ? aLayout.y : Number.MAX_SAFE_INTEGER;
    const by = Number.isFinite(bLayout?.y) ? bLayout.y : Number.MAX_SAFE_INTEGER;
    if (ay !== by) return ay - by;

    const ax = Number.isFinite(aLayout?.x) ? aLayout.x : Number.MAX_SAFE_INTEGER;
    const bx = Number.isFinite(bLayout?.x) ? bLayout.x : Number.MAX_SAFE_INTEGER;
    if (ax !== bx) return ax - bx;

    return aIndex - bIndex;
  });

  const getMobileWidgetHeight = (widgetId) => {
    const item = layout.find((layoutItem) => layoutItem.i === widgetId);
    const h = Number.isFinite(item?.h) ? Math.max(1, item.h) : 4;

    return Math.max(
      280,
      h * rowHeight + Math.max(0, h - 1) * 24
    );
  };

  return (
    <div style={pageWrapStyle}>
      <div className="w-full" style={{ minWidth: 0 }}>
        <div
          className={isMobile ? undefined : "flex justify-between items-center mb-1"}
          style={{
            minHeight: isMobile ? "auto" : headerHeight,
            height: isMobile ? "auto" : headerHeight,
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            alignItems: isMobile ? "stretch" : "center",
            justifyContent: "space-between",
            gap: isMobile ? 10 : undefined,
            paddingTop: isMobile ? 8 : undefined,
            paddingBottom: isMobile ? 10 : undefined,
            marginBottom: isMobile ? 4 : undefined,
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
            <h2
              className={isMobile ? undefined : "text-2xl font-semibold"}
              style={{
                fontSize: isMobile ? "1.35rem" : undefined,
                fontWeight: 600,
                lineHeight: 1.2,
                margin: 0,
              }}
            >
              Opportunities
            </h2>
            <div
              style={{
                fontSize: isMobile ? 11 : 12,
                opacity: 0.75,
                marginTop: isMobile ? 3 : undefined,
                lineHeight: 1.35,
              }}
            >
              {me?.username ? `Signed in as ${me.username}` : "Signed in"}
              {actorResourceId ? ` · Resource ${actorResourceId}` : " · (resource unresolved → view-only)"}
            </div>
          </div>

          <div
            style={{
              display: "flex",
              gap: isMobile ? 8 : 10,
              alignItems: "center",
              flexWrap: isMobile ? "wrap" : "nowrap",
              width: isMobile ? "100%" : "auto",
            }}
          >
            <button
              type="button"
              className="bg-btnGeneralNormal hover:bg-btnGeneralHover active:bg-btnGeneralClick text-white"
              style={{
                borderRadius: 10,
                padding: isMobile ? "10px 12px" : "10px 14px",
                minHeight: isMobile ? 44 : undefined,
                flex: isMobile ? "1 1 145px" : undefined,
                fontWeight: 800,
              }}
              onClick={() => setShowCreate(true)}
            >
              New Opportunity
            </button>

            <button
              type="button"
              className="bg-btnActionNormal hover:bg-btnActionHover active:bg-btnActionClick text-white"
              style={{
                borderRadius: 10,
                padding: isMobile ? "10px 12px" : "10px 14px",
                minHeight: isMobile ? 44 : undefined,
                flex: isMobile ? "1 1 120px" : undefined,
                fontWeight: 800,
              }}
              onClick={() => setShowAddModal(true)}
            >
              Add Widget
            </button>

            <button
              type="button"
              className="bg-btnActionNormal hover:bg-btnActionHover active:bg-btnActionClick text-white"
              style={{
                borderRadius: 10,
                padding: isMobile ? "10px 12px" : "10px 14px",
                minHeight: isMobile ? 44 : undefined,
                flex: isMobile ? "1 1 145px" : undefined,
                fontWeight: 800,
                opacity: selectedId ? 1 : 0.5,
              }}
              onClick={() => selectedId && setShowAccess(true)}
              disabled={!selectedId}
              title={!selectedId ? "Select an opportunity" : "Manage access"}
            >
              Share / Access
            </button>
          </div>
        </div>

        <div ref={gridRef} className="w-full" style={{ minWidth: 0 }}>
          {isMobile ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 12,
                width: "100%",
                minWidth: 0,
                paddingBottom: 8,
              }}
            >
              {mobileWidgetEntries.map(([key, WidgetComp]) => (
                <div
                  key={key}
                  style={{
                    width: "100%",
                    minWidth: 0,
                    height: `${getMobileWidgetHeight(key)}px`,
                    overflow: "hidden",
                  }}
                >
                  <WidgetWrapper title={getWidgetTitle(key)} onRemove={() => handleRemoveWidget(key)}>
                    {WidgetComp}
                  </WidgetWrapper>
                </div>
              ))}
            </div>
          ) : (
            <GridLayout
              className="layout"
              layout={layout}
              cols={12}
              rowHeight={rowHeight}
              width={gridWidth}
              draggableHandle=".widget-drag-handle"
              resizeHandles={["se"]}
              isResizable
              isDraggable
              onLayoutChange={onLayoutChange}
              compactType={null}
              preventCollision={false}
              margin={[16, 24]}
            >
              {Object.entries(widgets).map(([key, WidgetComp]) => (
                <div key={key} style={{ height: "100%", minHeight: 0 }}>
                  <WidgetWrapper title={getWidgetTitle(key)} onRemove={() => handleRemoveWidget(key)}>
                    {WidgetComp}
                  </WidgetWrapper>
                </div>
              ))}
            </GridLayout>
          )}
        </div>

        {showAddModal && (
          <AddWidgetModal
            widgets={widgetCatalog}
            onClose={() => setShowAddModal(false)}
            onSelect={handleAddWidget}
          />
        )}

        <OpportunityCreateModal
          open={showCreate}
          onClose={() => setShowCreate(false)}
          onCreate={async (payload) => {
            await onCreate(payload);
            setShowCreate(false);
          }}
        />

        <OpportunityAccessModal
          open={showAccess}
          onClose={() => setShowAccess(false)}
          opportunity={selected}
          accessRows={accessRows}
          loading={accessLoading}
          error={accessErr}
          canEdit={canEdit}
          onShare={async (resourceId, canEditFlag) => {
            if (!selected?.id) return;
            await onShare(selected.id, resourceId, canEditFlag);
          }}
          onRevoke={async (resourceId, reason) => {
            if (!selected?.id) return;
            await onRevoke(selected.id, resourceId, reason);
          }}
          onRefresh={async () => selected?.id && loadAccess(selected.id)}
        />
      </div>
    </div>
  );
}
